import XLink from '@/Components/XLink'
import useLocation from '@/Hooks/useLocation'
import XPhoneInput from '@/Mantine/XPhoneInput'
import { RootState } from '@/Store'
import {
  updateCurrentStep,
  updateLead,
  updateLeadId,
  updateVisitorCheck,
  updateVisitorPhone,
} from '@/Store/leadFormSlice'
import { Button, HStack, Typography, VStack, XelaColor } from '@/XelaReact'
import { IconArrowRight } from '@tabler/icons-react'
import axios from 'axios'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import LeadForm = App.Models.LeadForm

interface FormProps {
  agency_id: number
  source_id: number
  phone: string
  country: string
  country_code: string
  country_calling_code: string
  visitor_log_id: string
  lead_type: string
}

const VisitorForm: FC<{ leadForm: LeadForm; csrfToken: string }> = ({ leadForm, csrfToken }) => {
  const location = useLocation()
  const dispatch = useDispatch()
  const leadFormStore = useSelector((state: RootState) => state.leadForm)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState<FormProps>({
    agency_id: leadForm.agency_id,
    source_id: leadForm.id,
    phone: '',
    country: '',
    country_code: '',
    country_calling_code: '',
    visitor_log_id: '-1',
    lead_type: 'App\\Models\\Lead',
  })

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      country: location['country_name'],
      country_code: location['country_code'],
      country_calling_code: location['country_calling_code'],
    }))
  }, [location])

  const handleSubmit = async () => {
    console.log('submitting')
    setLoading(true)
    try {
      const response = await axios.post('/leads/form/visitor-logging', {
        ...formData,
        _token: csrfToken,
      })

      const { action, payload } = response.data

      if (action === 'visitor_log:checked' || action === 'visitor_log:skipped') {
        if (payload.lead) {
          dispatch(
            updateVisitorCheck({
              visitor_check: true,
              is_visitor: true,
            })
          )

          dispatch(updateLead(payload.lead))
          dispatch(updateLeadId(payload.lead.id.toString()))
          dispatch(updateCurrentStep(leadFormStore.current_step))
        } else {
          dispatch(
            updateVisitorCheck({
              visitor_check: true,
              is_visitor: false,
            })
          )

          dispatch(
            updateVisitorPhone({
              visitor_phone: payload.phone,
              visitor_country: formData.country,
              visitor_country_code: formData.country_code,
              visitor_country_calling_code: formData.country_calling_code,
            })
          )
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setErrors(error.response.data.errors || { phone: 'An error occurred' })
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <VStack
      spacing="24px"
      width="auto"
      bg={XelaColor.White}
      borderRadius="32px"
      style={{
        padding: '48px',
        margin: '24px',
        minHeight: '350px',
      }}
    >
      <VStack alignItems="center" spacing="8px">
        <Typography variant="title3">{leadForm.agency?.agency_name}</Typography>
        <Typography color={XelaColor.Gray6} variant="body-small-bold">
          Enter your phone number to continue
        </Typography>
      </VStack>
      <VStack spacing="24px">
        <VStack spacing="10px">
          <XPhoneInput
            error={errors.phone !== '' ? (errors.phone !== 'phone-not-exists' ? errors.phone : undefined) : undefined}
            dialCode={formData.country_calling_code}
            countryCode={formData.country_code}
            initialValue={formData.phone}
            onChange={(number, dialCode, countryCode, country) => {
              setFormData((prev) => ({
                ...prev,
                phone: number,
                country_calling_code: dialCode,
                country_code: countryCode,
                country: country,
              }))
            }}
          />
          {errors.phone === 'phone-not-exists' && (
            <Typography as="p" color={XelaColor.Red3} variant="caption" style={{ width: '344px' }}>
              This account cannot be found.
            </Typography>
          )}
        </VStack>
        <Button
          autoresize={false}
          label="Submit"
          disabled={loading}
          rightIcon={<IconArrowRight stroke={1} />}
          onClick={handleSubmit}
        />
      </VStack>
      <HStack justifyContent="center" spacing="4px">
        <Typography variant="caption">By clicking you are agreeing to</Typography>
        <Typography variant="button-small" color={XelaColor.Blue3}>
          <XLink href="#">Terms & Conditions</XLink>
        </Typography>
      </HStack>
    </VStack>
  )
}

export default VisitorForm
